import React from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { User, Music, Headphones, MapPin } from "lucide-react";

export interface DemographicFormData {
  age?: number;
  ageRange?: string;
  musicalGenres: string[];
  musicalEngagement: 'casual' | 'enthusiast' | 'musician' | 'professional';
  formalTrainingYears: number;
  listeningDevice: string;
  listeningContext: 'alone' | 'with-others';
  environment: string;
  aiMusicExperience: string;
  email?: string;
  acceptTerms: boolean;
}

interface DemographicsFormProps {
  onSubmit: (data: DemographicFormData) => void;
  onError?: (error: string) => void;
  isLoading?: boolean;
}

const MUSICAL_GENRES = [
  'Rock',
  'Pop',
  'Hip-Hop',
  'Classical',
  'Electronic',
  'Jazz',
  'Country',
  'R&B',
  'Alternative',
  'Folk',
  'Blues',
  'Reggae',
  'Metal',
  'Punk',
  'Indie',
  'Other'
];

const LISTENING_DEVICES = [
  'Headphones (over-ear)',
  'Headphones (on-ear)',
  'Earbuds/In-ear',
  'Laptop/phone speakers',
  'External speakers',
  'Other'
];

const ENVIRONMENTS = [
  'Quiet room',
  'Public transportation',
  'On the street/outdoors',
  'Café/bar/venue',
  'Office/workplace',
  'Other'
];

const AI_EXPERIENCE_OPTIONS = [
  'Never used AI music tools',
  'Heard about it but never tried',
  'Tried once or twice',
  'Use occasionally',
  'Use regularly',
  'Professional experience with AI music'
];

export function DemographicsForm({ onSubmit, onError, isLoading = false }: DemographicsFormProps) {
  const form = useForm<DemographicFormData>({
    defaultValues: {
      age: undefined,
      ageRange: '',
      musicalGenres: [],
      formalTrainingYears: 0,
      musicalEngagement: 'casual',
      listeningContext: 'alone',
      listeningDevice: '',
      environment: '',
      aiMusicExperience: '',
      email: '',
      acceptTerms: false,
    },
  });

  const [selectedGenres, setSelectedGenres] = React.useState<string[]>([]);
  const [otherGenre, setOtherGenre] = React.useState('');
  const [otherDevice, setOtherDevice] = React.useState('');
  const [otherEnvironment, setOtherEnvironment] = React.useState('');

  const handleSubmit = (data: DemographicFormData) => {
    try {
      // Validate required fields
      if (!data.musicalEngagement) {
        throw new Error('Musical engagement level is required');
      }

      if (!data.listeningDevice) {
        throw new Error('Listening device is required');
      }

      if (!data.listeningContext) {
        throw new Error('Listening context is required');
      }

      if (!data.environment) {
        throw new Error('Environment is required');
      }

      if (!data.aiMusicExperience) {
        throw new Error('AI music experience is required');
      }

      if (!data.acceptTerms) {
        throw new Error('You must accept the terms and conditions to continue');
      }

      // Include selected genres and other inputs
      const finalGenres = [...selectedGenres];
      if (otherGenre.trim()) {
        finalGenres.push(otherGenre.trim());
      }

      const finalData: DemographicFormData = {
        ...data,
        musicalGenres: finalGenres,
        listeningDevice: data.listeningDevice === 'Other' ? otherDevice : data.listeningDevice,
        environment: data.environment === 'Other' ? otherEnvironment : data.environment,
      };

      onSubmit(finalData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Form validation failed';
      onError?.(errorMessage);
    }
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Participant Information
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Please provide some information about yourself to help us understand how different backgrounds influence music perception.
          </p>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              
              {/* Age Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Demographics
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="age"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Age (optional)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="e.g., 25"
                            min="13"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ageRange"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Age Range (if you prefer not to specify exact age)</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select age range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="13-17">13-17</SelectItem>
                            <SelectItem value="18-24">18-24</SelectItem>
                            <SelectItem value="25-34">25-34</SelectItem>
                            <SelectItem value="35-44">35-44</SelectItem>
                            <SelectItem value="45-54">45-54</SelectItem>
                            <SelectItem value="55-64">55-64</SelectItem>
                            <SelectItem value="65+">65+</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Musical Background Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Musical Background
                </h3>

                <FormItem>
                  <FormLabel>Musical Genre Preferences (select all that apply)</FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {MUSICAL_GENRES.filter(g => g !== 'Other').map((genre) => (
                      <label
                        key={genre}
                        className={`flex items-center space-x-2 p-2 rounded-md border cursor-pointer transition-colors ${
                          selectedGenres.includes(genre)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'hover:bg-muted border-input'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGenres.includes(genre)}
                          onChange={() => toggleGenre(genre)}
                          className="sr-only"
                        />
                        <span className="text-sm">{genre}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-2">
                    <Input
                      placeholder="Other genres (separate with commas)"
                      value={otherGenre}
                      onChange={(e) => setOtherGenre(e.target.value)}
                    />
                  </div>
                  <FormDescription>
                    Select the genres you enjoy listening to most often.
                  </FormDescription>
                </FormItem>

                <FormField
                  control={form.control}
                  name="musicalEngagement"
                  rules={{ required: "Musical engagement level is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Musical Engagement Level *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your level of musical engagement" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="casual">Casual listener</SelectItem>
                          <SelectItem value="enthusiast">Enthusiast</SelectItem>
                          <SelectItem value="musician">Musician</SelectItem>
                          <SelectItem value="professional">Music professional</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="formalTrainingYears"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Years of Formal Musical Training</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="0"
                          min="0"
                          max="50"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormDescription>
                        Enter 0 if you have no formal musical training.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Listening Environment Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Headphones className="h-4 w-4" />
                  Listening Environment
                </h3>

                <FormField
                  control={form.control}
                  name="listeningDevice"
                  rules={{ required: "Listening device is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Listening Device *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your listening device" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {LISTENING_DEVICES.map((device) => (
                            <SelectItem key={device} value={device}>
                              {device}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {field.value === 'Other' && (
                        <Input
                          placeholder="Please specify your listening device"
                          value={otherDevice}
                          onChange={(e) => setOtherDevice(e.target.value)}
                          className="mt-2"
                        />
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="listeningContext"
                  rules={{ required: "Listening context is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Listening Context *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your listening context" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="alone">Alone</SelectItem>
                          <SelectItem value="with-others">With others</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="environment"
                  rules={{ required: "Environment is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Environment *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your current environment" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ENVIRONMENTS.map((env) => (
                            <SelectItem key={env} value={env}>
                              {env}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {field.value === 'Other' && (
                        <Input
                          placeholder="Please describe your environment"
                          value={otherEnvironment}
                          onChange={(e) => setOtherEnvironment(e.target.value)}
                          className="mt-2"
                        />
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* AI Experience Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  AI Music Experience
                </h3>

                <FormField
                  control={form.control}
                  name="aiMusicExperience"
                  rules={{ required: "AI music experience is required" }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Experience with AI Music Creation Tools *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your experience level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {AI_EXPERIENCE_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        This includes tools like Suno, Udio, AIVA, or other AI music generators.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address (optional, for reward tracking)</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your.email@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Provide your email to track your annotation progress and claim your $20 Tensorpool reward after completing 2 annotations.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-4 pt-4 border-t">
                <FormField
                  control={form.control}
                  name="acceptTerms"
                  rules={{ required: "You must accept the terms and conditions to continue" }}
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start space-x-3">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-normal">
                            I consent to the anonymous sharing of my annotation data for research and academic purposes under an open license
                          </FormLabel>
                          <FormDescription className="text-xs">
                            Your personal information and identity will remain strictly confidential. Only anonymized annotation responses will be shared to advance music perception research.
                          </FormDescription>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end pt-6">
                <Button 
                  type="submit" 
                  disabled={isLoading || !form.watch('acceptTerms')} 
                  className="min-w-32"
                >
                  {isLoading ? 'Submitting...' : 'Continue to Annotation'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}